// src/main.tsx
// Application entry point — mounts React with all providers.

import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { App } from '@/App'
import './index.css'

// ── TanStack Query client ──────────────────────────────────────
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Data is considered stale immediately on mount to ensure fresh data
      staleTime: 0,
      // Keep unused data in cache for 10 minutes
      gcTime: 1000 * 60 * 10,
      // Retry failed queries up to 2 times
      retry: 2,
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 10000),
      // Automatically refetch when component mounts
      refetchOnMount: true,
      // Refetch when window regains focus
      refetchOnWindowFocus: true,
      // Don't refetch on reconnect by default (can be overridden per-query)
      refetchOnReconnect: true,
    },
    mutations: {
      retry: 0,
    },
  },
})

// ── Mount ──────────────────────────────────────────────────────
const rootElement = document.getElementById('root')
if (!rootElement) {
  throw new Error('Root element #root not found in index.html')
}

createRoot(rootElement).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <App />
      {import.meta.env.DEV && (
        <ReactQueryDevtools initialIsOpen={false} buttonPosition="bottom-left" />
      )}
    </QueryClientProvider>
  </StrictMode>
)
